import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CssBaseline from '@mui/material/CssBaseline';
import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import AddFirewallForm from './components/AddFirewallForm';
import FirewallList from './components/FirewallList';
import UserManagement from './components/UserManagement';
import DatabaseBackup from './pages/DatabaseBackup';
import LLMsManagement from './components/LLMsManagement';
import AddFirewall from './pages/AddFirewall';
import Annotate from './pages/Annotate';
import AnnotationStats from './pages/AnnotationStats';
import ProvenanceClassification from './pages/ProvenanceClassification';
import ProvenanceStats from './pages/ProvenanceStats';
import History from './pages/History';
import AIChat from './pages/AIChat';
import Syslog from './pages/Syslog';
import Soar from './pages/Soar';
import MVPOverview from './pages/MVPOverview';
import MarketValidation from './pages/MarketValidation';
import NSCATPitch from './pages/NSCATPitch';
import ProvisionalPatent from './pages/ProvisionalPatent';
import ResearchPaper from './pages/ResearchPaper';
import ResearchPaper2 from './pages/ResearchPaper2';
import RagGroundTruth from './pages/RagGroundTruth';
import StratifiedGroundTruth from './pages/StratifiedGroundTruth';
import Login from './pages/Login';
import { firewallService, ollamaService, openaiService, googleAiStudioService, openRouterService, groqService, llmService, syslogService } from './services/api';
import authService from './services/auth';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#5c6bc0',
      light: '#7986cb',
      dark: '#3949ab',
    },
    secondary: {
      main: '#26c6da',
      light: '#4dd0e1',
      dark: '#00acc1',
    },
    background: {
      default: '#0a0e1a',
      paper: '#111827',
    },
    text: {
      primary: '#e2e8f0',
      secondary: '#94a3b8',
    },
    divider: 'rgba(148, 163, 184, 0.12)',
    success: {
      main: '#22c55e',
    },
    warning: {
      main: '#f59e0b',
    },
    error: {
      main: '#ef4444',
    },
    info: {
      main: '#38bdf8',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: 'rgba(148, 163, 184, 0.12)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: '#1e293b',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: '#1e293b',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = authService.isAuthenticated();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const isAuthenticated = authService.isAuthenticated();
  const isAdmin = authService.isAdmin();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  const [firewalls, setFirewalls] = useState([]);
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [auditHistory, setAuditHistory] = useState([]);
  const [models, setModels] = useState([]);
  const [onlineModels, setOnlineModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [setupRequired, setSetupRequired] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const setupStatus = await authService.checkSetup();
      setSetupRequired(setupStatus.setup_required);
      setIsAuthenticated(authService.isAuthenticated());
      
      if (setupStatus.setup_required) {
        setLoading(false);
        return;
      }
      
      // Annotators reach only the annotation page, which loads its own data.
      // Skipping the dashboard fetch avoids a burst of calls they have no
      // permission for.
      if (authService.isAuthenticated() && !authService.isAnnotator()) {
        await fetchData();
      }
    } catch (err) {
      console.error('Auth check failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all data in parallel — optional services won't block page load
      const [firewallsData, auditHistoryResponse, modelsData, onlineModelsData, googleModelsData, openRouterModelsData, groqModelsData, llmStatusData, syslogSummaryData, researchMetrics] = await Promise.all([
        firewallService.listFirewalls(),
        firewallService.getAuditHistory(),
        ollamaService.getModels().catch(err => {
          console.warn('Ollama service unavailable:', err.message);
          return { models: [] };
        }),
        openaiService.getModels().catch(err => {
          console.warn('OpenAI service error:', err.message);
          return { models: [] };
        }),
        googleAiStudioService.getModels().catch(err => {
          console.warn('Google AI Studio service error:', err.message);
          return { models: [] };
        }),
        openRouterService.getModels().catch(err => {
          console.warn('OpenRouter service error:', err.message);
          return { models: [] };
        }),
        groqService.getModels().catch(err => {
          console.warn('Groq service error:', err.message);
          return { models: [] };
        }),
        llmService.getStatus().catch(err => {
          console.warn('LLM status service error:', err.message);
          return {};
        }),
        syslogService.getSummary().catch(err => {
          console.warn('Syslog summary error:', err.message);
          return { critical: 0, high: 0, medium: 0, low: 0 };
        }),
        firewallService.getResearchMetrics().catch(err => {
          console.warn('Research metrics error:', err.message);
          return null;
        }),
      ]);

      setFirewalls(firewallsData);
      setModels(modelsData.models || []);
      setOnlineModels(onlineModelsData.models || []);
      
      // Extract audits array from the paginated response
      const auditHistoryData = auditHistoryResponse.audits || [];
      
      // Calculate stats - First row matches Firewall Overview
      const totalFirewalls = firewallsData.length;
      const totalTypes = new Set(firewallsData.map(f => f.type)).size;
      const offlineModels = modelsData.models ? modelsData.models.length : 0;
      const onlineModels = onlineModelsData.models ? onlineModelsData.models.length : 0;
      const freeModels = (googleModelsData.models ? googleModelsData.models.length : 0) +
        (openRouterModelsData.models ? openRouterModelsData.models.length : 0) +
        (groqModelsData.models ? groqModelsData.models.length : 0);
      const aiVendors = Object.values(llmStatusData).filter(v => v?.configured).length;
      
      // Audit Overview data
      const auditSummary = auditHistoryResponse.summary || {};
      const directAudits = auditSummary.direct_audits || 0;
      const averageScore = auditSummary.average_score || 0;

      setStats({
        totalFirewalls,
        totalTypes,
        offlineModels,
        onlineModels,
        freeModels,
        aiVendors,
        directAudits,
        averageScore,
        // Alert counts from audit history
        criticalAlerts: syslogSummaryData.critical || 0,
        highAlerts: syslogSummaryData.high || 0,
        mediumAlerts: syslogSummaryData.medium || 0,
        lowAlerts: syslogSummaryData.low || 0,
        compliantAlerts: syslogSummaryData.compliant || 0,
        // Paper 1 DB-traceable research metrics
        researchMetrics,
      });

      // Format recent activity for the activity section
      const activity = auditHistoryData.slice(0, 5).map(audit => ({
        status: (audit.overall_score || 0) >= 80 ? 'success' : 'warning',
        description: `Audit completed for ${firewallsData.find(f => f.id === audit.firewall_id)?.device_name || 'Unknown Firewall'}`,
        timestamp: audit.audit_date,
      }));

      setRecentActivity(activity);
      setAuditHistory(auditHistoryData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSuccess = () => {
    fetchData();
  };

  const handleAuthChange = () => {
    setIsAuthenticated(authService.isAuthenticated());
    if (authService.isAuthenticated()) {
      fetchData();
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}>
          <div>Loading...</div>
        </div>
      </ThemeProvider>
    );
  }

  // Show login/setup if not authenticated or setup required
  if (setupRequired || !isAuthenticated) {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <Login onAuthChange={handleAuthChange} />
          </Router>
        </ThemeProvider>
      </LocalizationProvider>
    );
  }

  // Annotators get an isolated application: one route, no Layout, no
  // navigation, and a catch-all that returns them to it. The confinement is
  // here rather than on individual routes so no page added later can leak the
  // existing verdicts or the precision figures to someone mid-annotation.
  if (authService.isClassifier()) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            <Route path="/classifier" element={<ProvenanceClassification />} />
            <Route path="*" element={<Navigate to="/classifier" replace />} />
          </Routes>
        </Router>
      </ThemeProvider>
    );
  }

  if (authService.isAnnotator()) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            <Route path="/annotate" element={<Annotate />} />
            <Route path="*" element={<Navigate to="/annotate" replace />} />
          </Routes>
        </Router>
      </ThemeProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Layout>
            <Routes>
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Dashboard
                      stats={stats}
                      recentActivity={recentActivity}
                      auditHistory={auditHistory}
                      firewalls={firewalls}
                      loading={loading}
                      error={error}
                      onAddClick={() => setIsAddDialogOpen(true)}
                      refresh={fetchData}
                    />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/firewalls"
                element={
                  <ProtectedRoute>
                    <FirewallList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/firewalls/add"
                element={
                  <ProtectedRoute>
                    <AddFirewall />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users"
                element={
                  <AdminRoute>
                    <UserManagement />
                  </AdminRoute>
                }
              />
              <Route
                path="/database-backup"
                element={
                  <AdminRoute>
                    <DatabaseBackup />
                  </AdminRoute>
                }
              />
              <Route
                path="/mvp-overview"
                element={
                  <AdminRoute>
                    <MVPOverview />
                  </AdminRoute>
                }
              />
              <Route
                path="/mvp-overview/market-validation"
                element={
                  <AdminRoute>
                    <MarketValidation />
                  </AdminRoute>
                }
              />
              <Route
                path="/mvp-overview/nscat-pitch"
                element={
                  <AdminRoute>
                    <NSCATPitch />
                  </AdminRoute>
                }
              />
              <Route
                path="/mvp-overview/provisional-patent"
                element={
                  <AdminRoute>
                    <ProvisionalPatent />
                  </AdminRoute>
                }
              />
              <Route
                path="/mvp-overview/research-paper"
                element={
                  <AdminRoute>
                    <ResearchPaper />
                  </AdminRoute>
                }
              />
              <Route
                path="/mvp-overview/research-paper-2"
                element={
                  <AdminRoute>
                    <ResearchPaper2 />
                  </AdminRoute>
                }
              />
              <Route
                path="/mvp-overview/rag-ground-truth"
                element={
                  <AdminRoute>
                    <RagGroundTruth />
                  </AdminRoute>
                }
              />
              <Route
                path="/mvp-overview/stratified-ground-truth"
                element={
                  <AdminRoute>
                    <StratifiedGroundTruth />
                  </AdminRoute>
                }
              />
              <Route
                path="/mvp-overview/paper1-annotation-stats"
                element={
                  <AdminRoute>
                    <AnnotationStats />
                  </AdminRoute>
                }
              />
              <Route
                path="/mvp-overview/ai-inferred-stats"
                element={
                  <AdminRoute>
                    <ProvenanceStats />
                  </AdminRoute>
                }
              />
              <Route
                path="/history"
                element={
                  <ProtectedRoute>
                    <History />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ai-chat"
                element={
                  <ProtectedRoute>
                    <AIChat />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/syslog"
                element={
                  <ProtectedRoute>
                    <Syslog />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/soar"
                element={
                  <ProtectedRoute>
                    <Soar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/llms"
                element={
                  <ProtectedRoute>
                    <LLMsManagement readOnly={!authService.isAdmin()} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/login"
                element={<Navigate to="/" replace />}
              />
            </Routes>
          </Layout>
          <AddFirewallForm
            open={isAddDialogOpen}
            onClose={() => setIsAddDialogOpen(false)}
            onSuccess={handleAddSuccess}
          />
        </Router>
      </ThemeProvider>
    </LocalizationProvider>
  );
}

export default App; 